import type {
  AttemptInput,
  AttemptResult,
  Difficulty,
  LeaderboardEntry,
  QuizMode,
  QuizRun,
  QuizSummary,
  SessionAccount,
} from "@mathpop/contracts";

interface ErrorResponse {
  error?: { message?: string };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const mutation =
    init?.method &&
    !["GET", "HEAD", "OPTIONS"].includes(init.method.toUpperCase());
  const response = await fetch(`/api/v1${path}`, {
    credentials: "same-origin",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(mutation ? { "idempotency-key": crypto.randomUUID() } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(
      body.error?.message ?? `Request failed (${response.status})`,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  session: () => request<{ account: SessionAccount | null }>("/auth/session"),
  sessions: () =>
    request<{
      sessions: Array<{
        id: string;
        deviceLabel: string;
        createdAt: string;
        lastSeenAt: string;
        expiresAt: string;
        current: boolean;
      }>;
    }>("/auth/sessions"),
  revokeOtherSessions: () =>
    request<{ revoked: number }>("/auth/sessions/revoke-others", {
      method: "POST",
      body: "{}",
    }),
  exportAccount: () => request<Record<string, unknown>>("/account/export"),
  updateProfile: (displayName: string, leaderboardOptIn: boolean) =>
    request<{ account: SessionAccount }>("/account/profile", {
      method: "PATCH",
      body: JSON.stringify({ displayName, leaderboardOptIn }),
    }),
  withdrawConsent: () =>
    request<{ withdrawn: true; leaderboardOptIn: false }>(
      "/account/consent/withdraw",
      { method: "POST", body: "{}" },
    ),
  logout: () => request<void>("/auth/logout", { method: "POST", body: "{}" }),
  startQuiz: (profileId: string, mode: QuizMode, difficulty: Difficulty) =>
    request<QuizRun>("/quiz-runs", {
      method: "POST",
      body: JSON.stringify({ profileId, mode, difficulty }),
    }),
  answer: (runId: string, attempt: AttemptInput) =>
    request<AttemptResult>(`/quiz-runs/${runId}/attempts`, {
      method: "POST",
      body: JSON.stringify(attempt),
    }),
  complete: (runId: string) =>
    request<QuizSummary>(`/quiz-runs/${runId}/complete`, {
      method: "POST",
      body: "{}",
    }),
  leaderboard: (difficulty: Difficulty, mode: QuizMode) =>
    request<{ entries: LeaderboardEntry[] }>(
      `/leaderboards/maths?difficulty=${difficulty}&mode=${mode}`,
    ),
};
